// FAQ content for different page types

export interface FAQ {
  question: string;
  answer: string;
}

// Pillar page FAQs
export const PILLAR_FAQS: FAQ[] = [
  {
    question: 'What types of business funding are available?',
    answer: 'Common business funding options include working capital loans, lines of credit, term loans, equipment financing, and commercial real estate loans. Each type serves different business needs and has varying qualification requirements.'
  },
  {
    question: 'How do I know which funding type is right for my business?',
    answer: 'The right funding type depends on your specific needs, time in business, monthly revenue, credit profile, and how you plan to use the funds. Our pre-qualification tool helps match you with appropriate options based on your business profile.'
  },
  {
    question: 'What are typical qualification requirements?',
    answer: 'Most lenders look for businesses operating for at least 12 months with minimum monthly revenue of $30,000. Credit requirements vary by product, with some options available for credit scores as low as 600. Specific requirements depend on the funding type and amount.'
  },
  {
    question: 'How long does the funding process take?',
    answer: 'Timeline varies by funding type. Working capital and lines of credit can be approved in 24-72 hours with funding in 3-5 business days. Term loans and equipment financing typically take 5-14 days. SBA loans and commercial real estate financing may take 60-90 days.'
  },
  {
    question: 'Is this platform a lender?',
    answer: 'No, we are an educational platform that helps businesses understand their funding options. We are not a lender and do not make credit decisions. We may refer qualified businesses to our partner network.'
  }
];

// City-specific FAQs (template - inject city name)
export function getCityFAQs(cityName: string): FAQ[] {
  return [
    {
      question: `What business funding options are available in ${cityName}?`,
      answer: `Businesses in ${cityName} have access to the same range of funding options as businesses nationwide, including working capital, lines of credit, term loans, equipment financing, and commercial real estate loans. Local economic conditions and industry mix may influence which products are most commonly used.`
    },
    {
      question: `Do I need to be located in ${cityName} to use this service?`,
      answer: `While we provide city-specific resources for ${cityName} businesses, our partner network serves businesses across the United States. The pre-qualification process is the same regardless of location.`
    },
    {
      question: `How quickly can ${cityName} businesses get funded?`,
      answer: `Funding timelines are consistent nationwide. Working capital and lines of credit typically fund within 3-5 business days after approval. Term loans and equipment financing take 5-14 days. SBA and commercial real estate loans require 60-90 days.`
    },
    {
      question: `What documents do ${cityName} businesses need to apply?`,
      answer: `Standard documentation includes business bank statements (3-6 months), business tax returns, profit & loss statements, and a valid business license. Specific requirements vary by funding type and lender.`
    },
    {
      question: `Are there special programs for ${cityName} businesses?`,
      answer: `While our platform focuses on nationwide funding options, some lenders in our network may offer programs specific to certain regions or industries common in ${cityName}. These are identified during the full application process.`
    }
  ];
}

// Prequalification page FAQs
export function getPrequalificationFAQs(cityName?: string): FAQ[] {
  const cityPrefix = cityName ? `${cityName} ` : '';
  
  return [
    {
      question: 'What is pre-qualification?',
      answer: 'Pre-qualification is an initial assessment of your business profile to determine which funding options you may be eligible for. It does not guarantee approval but helps identify the most appropriate products for your situation.'
    },
    {
      question: `How long does the ${cityPrefix}pre-qualification take?`,
      answer: 'The pre-qualification questionnaire takes 2-3 minutes to complete. You will receive immediate feedback on potential funding options based on your answers.'
    },
    {
      question: 'Does pre-qualification affect my credit score?',
      answer: 'No, our pre-qualification process does not require a credit check and will not impact your credit score. Credit checks only occur when you submit a full application with a lender.'
    },
    {
      question: 'What happens after I complete pre-qualification?',
      answer: 'If you meet initial requirements, you will be directed to continue with a secure full application. The lender will review your complete business profile and documentation to make a final credit decision.'
    },
    {
      question: 'What if I do not qualify?',
      answer: 'If you do not meet minimum requirements, we will explain the specific reasons and may suggest alternative options or steps you can take to improve your eligibility in the future.'
    },
    {
      question: 'Is my information secure?',
      answer: 'Yes, all information is transmitted securely. We do not store sensitive personal or financial information. Your data is only shared with lenders you choose to apply with.'
    }
  ];
}

// Funding type specific FAQs
export function getFundingTypeFAQs(fundingType: string, cityName: string): FAQ[] {
  const baseQuestions = [
    {
      question: `What is ${formatFundingTypeName(fundingType)}?`,
      answer: getFundingTypeDescription(fundingType)
    },
    {
      question: `Who qualifies for ${formatFundingTypeName(fundingType)} in ${cityName}?`,
      answer: getFundingTypeQualifications(fundingType)
    },
    {
      question: `How much can ${cityName} businesses borrow with ${formatFundingTypeName(fundingType)}?`,
      answer: getFundingTypeAmounts(fundingType)
    },
    {
      question: `What are typical terms for ${formatFundingTypeName(fundingType)}?`,
      answer: getFundingTypeTerms(fundingType)
    },
    {
      question: `How do I apply for ${formatFundingTypeName(fundingType)} in ${cityName}?`,
      answer: `Start with our pre-qualification tool to see if you meet initial requirements. If qualified, you will be directed to complete a full application with detailed business and financial information.`
    }
  ];

  return baseQuestions;
}

function formatFundingTypeName(slug: string): string {
  const names: Record<string, string> = {
    'working-capital': 'Working Capital',
    'line-of-credit': 'Line of Credit',
    'term-loans': 'Term Loans',
    'equipment-financing': 'Equipment Financing',
    'commercial-real-estate': 'Commercial Real Estate Financing'
  };
  return names[slug] || slug;
}

function getFundingTypeDescription(fundingType: string): string {
  const descriptions: Record<string, string> = {
    'working-capital': 'Working capital financing provides short-term funds to cover day-to-day operational expenses such as payroll, inventory, rent, and utilities. These loans help businesses manage cash flow gaps and seasonal fluctuations.',
    'line-of-credit': 'A business line of credit is a revolving credit facility that allows you to draw funds as needed up to a predetermined limit. You only pay interest on the amount you use, making it ideal for managing variable expenses and opportunities.',
    'term-loans': 'Term loans provide a lump sum of capital repaid over a fixed period with regular payments. They are suitable for significant investments like expansion, equipment purchases, or refinancing existing debt.',
    'equipment-financing': 'Equipment financing allows businesses to purchase or lease equipment with the equipment itself serving as collateral. This preserves working capital while acquiring necessary machinery, vehicles, or technology.',
    'commercial-real-estate': 'Commercial real estate financing helps businesses purchase, refinance, or renovate commercial property. These loans typically have longer terms and may be structured as conventional loans or SBA 504 loans.'
  };
  return descriptions[fundingType] || 'Business financing option.';
}

function getFundingTypeQualifications(fundingType: string): string {
  const qualifications: Record<string, string> = {
    'working-capital': 'Typically requires 12+ months in business, $30,000+ monthly revenue, and credit scores of 600+. Lenders review bank statements and revenue trends.',
    'line-of-credit': 'Usually requires 12-24+ months in business, $50,000+ monthly revenue, and credit scores of 650+. Strong banking history and consistent deposits are important.',
    'term-loans': 'Generally requires 24+ months in business, $50,000+ monthly revenue, and credit scores of 650+. Lenders evaluate profitability, debt service coverage, and business stability.',
    'equipment-financing': 'Often available to businesses with 12+ months operating history and credit scores of 600+. The equipment serves as collateral, which can make approval easier.',
    'commercial-real-estate': 'Typically requires 24+ months in business, strong credit (680+), significant down payment (10-30%), and detailed financial documentation. SBA options may have more flexible terms.'
  };
  return qualifications[fundingType] || 'Qualification requirements vary by lender.';
}

function getFundingTypeAmounts(fundingType: string): string {
  const amounts: Record<string, string> = {
    'working-capital': 'Working capital loans typically range from $50,000 to $500,000, with some lenders offering up to $1 million for well-qualified businesses.',
    'line-of-credit': 'Business lines of credit commonly range from $10,000 to $250,000, with larger lines available for established businesses with strong financials.',
    'term-loans': 'Term loans can range from $50,000 to $5 million or more, depending on business qualifications, collateral, and intended use of funds.',
    'equipment-financing': 'Equipment financing typically covers 80-100% of equipment cost, with loan amounts ranging from $25,000 to $5 million depending on the equipment value.',
    'commercial-real-estate': 'Commercial real estate loans can range from $250,000 to $20 million or more. SBA 504 loans can go up to $5.5 million for standard projects.'
  };
  return amounts[fundingType] || 'Loan amounts vary based on qualifications.';
}

function getFundingTypeTerms(fundingType: string): string {
  const terms: Record<string, string> = {
    'working-capital': 'Terms typically range from 3 to 18 months, with daily or weekly repayment schedules common. Some products offer longer terms up to 24 months.',
    'line-of-credit': 'Lines of credit are usually structured as 6-12 month revolving facilities that can be renewed. You draw and repay as needed throughout the term.',
    'term-loans': 'Term loans typically have repayment periods of 1 to 10 years, with monthly payments. Longer terms are available for larger amounts and real estate-backed loans.',
    'equipment-financing': 'Equipment financing terms typically match the useful life of the equipment, ranging from 1 to 7 years. Monthly or quarterly payments are standard.',
    'commercial-real-estate': 'Commercial real estate loans typically have terms of 5 to 25 years, often with amortization periods up to 25-30 years. SBA 504 loans offer up to 25-year terms.'
  };
  return terms[fundingType] || 'Terms vary by lender and loan type.';
}
